import './globals.d.ts';

const COOKIE_NAME = 'aw_user_is_corp_admin';
const COOKIE_HOURS = 3;
const EVENT_NAME = 'adw:corpAdminResolved';

const setCorpAdminCookie = (value: string): void => {
    const expires = new Date(Date.now() + COOKIE_HOURS * 60 * 60 * 1000);
    window.Cookies?.set(COOKIE_NAME, value, { expires, path: '/', sameSite: 'Strict', secure: true });
};

const deleteCorpAdminCookie = (): void => {
    window.Cookies?.remove(COOKIE_NAME, { path: '/' });
};

/**
 * Dispatches `adw:corpAdminResolved` on `document`.
 * detail.isCorpAdmin {boolean} — whether the current user is a corporate account admin.
 * detail.fromCache   {boolean} — true when the value came from the cookie, false after a live API call.
 *
 * Usage:
 *   document.addEventListener('adw:corpAdminResolved', ({ detail }) => {
 *     if (detail.isCorpAdmin) { ... }
 *   });
 */
const applyCorpAdminVisibility = (isCorpAdmin: boolean): void => {
    if (!isCorpAdmin) {
        document.querySelectorAll<HTMLElement>('.corp-admin-link').forEach(el => {
            el.classList.add('d-none');
        });
    }
};

const dispatchResolved = (isCorpAdmin: boolean, fromCache: boolean): void => {
    applyCorpAdminVisibility(isCorpAdmin);
    document.dispatchEvent(
        new CustomEvent<{ isCorpAdmin: boolean; fromCache: boolean }>( EVENT_NAME, {
            detail: { isCorpAdmin, fromCache },
        } )
    );
};

const fetchAndCacheAdminStatus = async (): Promise<void> => {
    if (Object.keys(window.Zephr || {}).length === 0) return;

    // Check if the user has any active Zephr products with "corp" in the name before making the API call
    if( window.Zephr?.accessDetails?.activeProducts?.some( (name: string) => name.toLowerCase?.().includes( 'corp' ) ) ){
        try {
            const response = await fetch('/wp-json/adw-zephr/v1/corp-admin-status', {
                method: 'GET',
                credentials: 'same-origin',
            });
            const data = await response.json() as { is_corp_admin: boolean };
            setCorpAdminCookie(data.is_corp_admin ? 'true' : 'false');
    
            dispatchResolved(data.is_corp_admin, false);
        } catch {
            // Silent fail — leave cookie unset so the next page load retries
        }
    } else {
        applyCorpAdminVisibility(false);
    }
};

const getParam = (name: string): string | null =>
    new URLSearchParams(window.location.search).get(name);

const removeUrlParam = (name: string, delayMs = 5000): void => {
    setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete(name);
        history.replaceState(null, '', url.toString());
    }, delayMs);
};

const init = (): void => {
    // ?signing_in=true — user just logged in with a fresh blaize_session.
    // Force a fresh API call by clearing any stale cookie, then clean up the param.
    if (getParam('signing_in') === 'true') {
        deleteCorpAdminCookie();
        removeUrlParam('signing_in');
        fetchAndCacheAdminStatus();
        return;
    }

    const cookieVal = window.Cookies?.get(COOKIE_NAME);

    if (cookieVal !== undefined) {
        const isCorpAdmin = cookieVal === 'true';
        dispatchResolved(isCorpAdmin, true);
        return;
    }

    // Cookie absent — call the REST API to determine admin status.
    // The API returns false for logged-out or non-admin users.
    fetchAndCacheAdminStatus();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
